import React, { useState, createContext, useContext, useEffect } from 'react'
import env from '../../config/configuration'

const initAgentState = {
  agent: '',
  responseEngine: '',
  knowledgeBase: '',
  error: '',
  loading: true,
}

const AgentContext = createContext(initAgentState)

const useAgent = () => {
  return useContext(AgentContext)
}

const getPrompt = (agent = {}) => {
  return `
  You are Gabbi, an AI-powered virtual assistant for ${
    agent?.bio?.companyName
  }, specializing in ${
    agent?.bio?.practiceArea != 'Other'
      ? agent?.bio?.practiceArea
      : agent?.bio?.practiceDesc
  }. Your goal is to professionally handle client inquiries, assist with appointment scheduling, and provide essential legal information without giving legal advice. You maintain a ${
    agent?.bio?.personality
  } and ${
    agent?.bio?.greeting
  } communication style, ensuring a smooth and engaging interaction.  

---

## **🎙️ General Behavior & Guidelines**  
- **Be Professional & Engaging** – Maintain a polite, confident, and knowledgeable tone.  
- **Do Not Provide Legal Advice** – Instead, guide callers toward booking a consultation.  
- **Follow Legal Compliance** – Ensure confidentiality, avoid speculative claims, and never promise outcomes.  

---

## **📞 Call Handling Workflow**  

1️⃣ **Initial Greeting & Identification**  
   - Answer every call with a ${agent?.bio?.greeting} tone.  
   - Example: _"Hello, this is Gabbi, the virtual assistant for ${
     agent?.bio?.companyName
   }. How may I assist you today?"

2️⃣ *Understanding the Inquiry*  
   - If the caller mentions *${
     agent?.bio?.practiceArea != 'Other'
       ? agent?.bio?.practiceArea
       : agent?.bio?.practiceDesc
   }*, respond with guidance. 
     
   - If they ask for a *consultation*, tell that you have booked an appointment and you will receive a confirmation soon

3️⃣ **Handling Pricing & Fees**  
   - If the caller asks, _"How much do your services cost?"_  
     - Respond with:  Our attorneys will assess your case and give you the cost


5️⃣ **Transferring Calls to an Attorney**  
the attorney is unavailable, tell them for that you’ll call them back when available


## **🗂️ Common Legal Inquiry Responses**  

- **Client:** "Do you handle {practise_arear}?"  
- **AI:** _"Yes, ${agent?.bio?.companyName} specializes in ${
    agent?.bio?.practiceArea != 'Other'
      ? agent?.bio?.practiceArea
      : agent?.bio?.practiceDesc
  }. Would you like to schedule a consultation to discuss your case?"_  

- **Client:** "Can you give me legal advice?"  
- **AI:** _"I’m not a licensed attorney, but I can schedule a consultation with one of our experts who can assist you."_

- **Client:** "How much do you charge?"  
- **AI:** Will be provided upon assessment 
- **Client:** "Where is your office located?"  
- **AI:** _"${agent?.bio?.address}"_  

---

## **🛑 Fallback & Edge Cases**  
- If the AI doesn’t recognize a question, respond with:  
  _"I’m here to help, but I’d recommend speaking with one of our attorneys for more details. Would you like me to book a consultation for you?"

- If a caller gets frustrated, remain calm and say:  
  _"I understand your concern. Let me see how I can best assist you. Would you like me to give you a call back later?
---

### **🔗 Final Call-to-Action**  
_"Before we end this call, is there anything else I can help you with? If you'd like, I can go ahead and book an appointment for you now."_  

---

## **🎯 Your Objective as Gabbi**  
✅ Provide a professional and friendly experience.  
✅ Ensure compliance with legal guidelines.  
✅ Seamlessly assist clients with scheduling and inquiries.  
✅ Never give legal advice—always guide callers toward consultations.  
  `
}

const AgentProvider = ({ children }) => {
  const userAgentInfo = JSON.parse(localStorage.getItem('agent'))
  const [agent, setAgent] = useState('')
  const [responseEngine, setResponseEngine] = useState('')
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const createKnowledgeBase = async () => {
    try {
      const response = await fetch(
        'https://api.retellai.com/list-knowledge-bases',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${env.RETELL_API}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      console.log('Knowledgebase created successfully:', data)
      setKnowledgeBase(data[0])
      return data[0]
    } catch (err) {
      setError(err.message)
      console.error('Error creating knowledge base:', err)
    }
  }

  const createResponseEngine = async () => {
    try {
      // const kbase = await createKnowledgeBase()
      const KBId = 'knowledge_base_e8907d6414ecb1e1' //'knowledge_base_0c1f8beb653a4672' ayaz bhai

      const response = await fetch(
        'https://api.retellai.com/create-retell-llm',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${env.RETELL_API}`,
          },
          body: JSON.stringify({
            knowledge_base_ids: [KBId],
            begin_message:
              agent.bio?.greeting ||
              'Hello, I am a virtual assistant. How can I help you today?',
            general_prompt: getPrompt(userAgentInfo),
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      console.log('Response engine created successfully:', data)
      setResponseEngine(data)
      return data
    } catch (err) {
      setError(err.message)
      console.error('Error creating response engine:', err)
    }
  }

  const createAgent = async () => {
    console.log('Creating agent...')
    try {
      const rEngine = await createResponseEngine()
      const response = await fetch('https://api.retellai.com/create-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${env.RETELL_API}`,
        },
        body: JSON.stringify({
          response_engine: {
            type: 'retell-llm',
            llm_id: rEngine.llm_id, //'llm_17180b5a8f5eddc67e8f2bbe82cc',
          }, //rEngine.llm_id
          voice_id: userAgentInfo.voiceId || '11labs-Jason',
        }),
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      console.log('Agent created successfully:', data)
      setAgent(data)
    } catch (err) {
      setError(err.message)
      console.error('Error creating agent:', err)
    } finally {
      setLoading(false)
    }
  }

  const deleteResponseEngine = async () => {
    try {
      // const rEngine = await createResponseEngine()
      const response = await fetch(
        'https://api.retellai.com/delete-retell-llm/' + responseEngine.llm_id,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${env.RETELL_API}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      console.log('Agent deleted successfully:', data)
      setAgent()
    } catch (err) {
      setError(err.message)
      console.error('Error deleting agent:', err)
    }
  }

  const deleteAgent = async () => {
    try {
      // const rEngine = await createResponseEngine()
      const response = await fetch(
        'https://api.retellai.com/delete-agent/' + agent.agent_id,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${env.RETELL_API}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      console.log('Agent deleted successfully:', data)
      setAgent()
    } catch (err) {
      setError(err.message)
      console.error('Error deleting agent:', err)
    }
  }

  useEffect(() => {
    return () => {
      if (responseEngine) deleteResponseEngine()
    }
  }, [responseEngine])

  useEffect(() => {
    return () => {
      if (agent) deleteAgent()
    }
  }, [agent])

  const onLoad = () => {
    createAgent()
  }

  useEffect(onLoad, [])

  return (
    <AgentContext.Provider
      value={{
        agent,
        responseEngine,
        knowledgeBase,
        error,
        loading,
      }}
    >
      {children}
    </AgentContext.Provider>
  )
}

export { AgentProvider, useAgent }
